<script setup lang="ts">
import { onMounted, ref } from 'vue';
import type { NotificationContent } from '../useNotifications';

const resolver = ref<Promise<void>>();
const timeoutCompletion = ref(0);
const start = ref(Date.now());

const props = withDefaults(
  defineProps<{
    message: NotificationContent;
    timeout?: number;
    level?: 'default' | 'warning' | 'danger' | 'success';
  }>(),
  {
    timeout: 1000,
    level: 'default',
  },
);

const emit = defineEmits<{
  (event: 'input'): void;
}>();

const completionInterval = setInterval(() => {
  if (!resolver.value) {
    return;
  }

  const elapsed = Date.now() - start.value;
  const completion = Math.min(100, (elapsed / props.timeout) * 100);

  timeoutCompletion.value = completion;

  if (completion >= 100) {
    clearInterval(completionInterval);
  }
}, 25);

onMounted(() => {
  resolver.value = new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, props.timeout);
  });

  resolver.value.then(() => {
    emit('input');
  });
});
</script>

<template>
  <div
    class="mx-4 max-w-md pointer-events-auto rounded-[--tml-nudge-border-radius] shadow-lg overflow-hidden"
    :class="{
      'bg-[--tml-nudge-background-color] text-[--tml-nudge-text-color]': level === 'default',
      'bg-[--tml-nudge-error-background-color] text-[--tml-nudge-error-text-color]':
        level === 'danger',
      'bg-[--tml-nudge-warning-background-color] text-[--tml-nudge-warning-text-color]':
        level === 'warning',
      'bg-[--tml-nudge-success-background-color] text-[--tml-nudge-success-text-color]':
        level === 'success',
    }"
  >
    <div class="p-[--tml-nudge-padding]">
      <template v-if="typeof message === 'string'">
        {{ message }}
      </template>
      <Component :is="message" v-else />
    </div>
    <div class="relative w-full h-0.5">
      <div
        class="absolute top-0 left-0 h-0.5"
        :class="{
          'bg-[--tml-nudge-completion-bar-color]': level === 'default',
          'bg-[--tml-nudge-error-completion-bar-color]': level === 'danger',
          'bg-[--tml-nudge-warning-completion-bar-color]': level === 'warning',
          'bg-[--tml-nudge-success-completion-bar-color]': level === 'success',
        }"
        :style="{ width: `${timeoutCompletion}%` }"
      />
    </div>
  </div>
</template>
